import React, { useMemo } from 'react';
import * as d3 from 'd3';
import {
  Grid,
  Paper,
  Typography,
  Box,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { ResizableTable } from '../../components/resizableTable/ResizableTable';
import { dateFormatterMomentV3 } from '../../util/customFunctions';

// Column definitions for Won Opportunities table
const createWonOppsColumns = () => [
  {
    label: '#',
    sortable: false,
    align: 'center',
    subColumns: [{
      label: '#',
      field: 'rowNum',
      type: 'number',
      align: 'center',
      width: 5,
      sortable: false,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Opportunity',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Opportunity',
      field: 'opportunity',
      type: 'text',
      align: 'left',
      width: 120,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Owner',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Owner',
      field: 'owner',
      type: 'text',
      align: 'left',
      width: 50,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'ACV ($K)',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'ACV ($K)',
      field: 'acvInK',
      type: 'number',
      align: 'right',
      width: 30,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Disc. %',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Disc. %',
      field: 'discountPercent',
      type: 'number',
      align: 'right',
      width: 20,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Won Date',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Won Date',
      field: 'wonDate',
      type: 'date',
      align: 'right',
      width: 40,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Age since Qual. (Days)',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Age since Qual. (Days)',
      field: 'ageSinceQualDays',
      type: 'number',
      align: 'right',
      width: 30,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Partner',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Partner',
      field: 'partner',
      type: 'text',
      align: 'left',
      width: 80,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
];

// Column definitions for Summary table
const createSummaryTableColumns = () => [
  {
    label: '',
    sortable: false,
    subColumns: [{
      label: '',
      field: 'label',
      type: 'text',
      align: 'left',
      width: 100,
      sortable: false,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: '# of Opps',
    sortable: false,
    subColumns: [{
      label: '# of Opps',
      field: 'count',
      type: 'number',
      align: 'right',
      width: 70,
      sortable: false,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'ACV ($M)',
    sortable: false,
    subColumns: [{
      label: 'ACV ($M)',
      field: 'acvInMillions',
      type: 'number',
      align: 'right',
      width: 70,
      sortable: false,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
];

// Column definitions for Forecast Opportunities table
const createForecastOppsColumns = () => [
  {
    label: '#',
    sortable: false,
    align: 'center',
    subColumns: [{
      label: '#',
      field: 'rowNum',
      type: 'number',
      align: 'left',
      width: 20,
      sortable: false,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Opportunity',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Opportunity',
      field: 'opportunity',
      type: 'text',
      align: 'left',
      width: 120,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Owner',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Owner',
      field: 'owner',
      type: 'text',
      align: 'left',
      width: 40,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Lead Source',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Lead Source',
      field: 'Lead_Source',
      type: 'text',
      align: 'left',
      width: 40,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'ACV ($K)',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'ACV ($K)',
      field: 'acvInK',
      type: 'number',
      align: 'right',
      width: 35,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Disc. %',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Disc. %',
      field: 'discountPercent',
      type: 'number',
      align: 'left',
      width: 20,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Proj. Close Date',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Proj. Close Date',
      field: 'projectedCloseDate',
      type: 'date',
      align: 'right',
      width: 42,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Age since Qual. (Days)',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Age since Qual. (Days)',
      field: 'ageSinceQualDays',
      type: 'number',
      align: 'right',
      width: 30,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Stage',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Stage',
      field: 'stageChar',
      type: 'text',
      align: 'center',
      width: 25,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Days in Stg.',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Days in Stg.',
      field: 'daysInStage',
      type: 'number',
      align: 'right',
      width: 20,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Forecast Category',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Forecast Category',
      field: 'forecastCategory',
      type: 'text',
      align: 'left',
      width: 50,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Partner',
    sortable: true,
    align: 'center',
    subColumns: [{
      label: 'Partner',
      field: 'partner',
      type: 'text',
      align: 'left',
      width: 70,
      sortable: true,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Risks',
    sortable: false,
    align: 'center',
    subColumns: [{
      label: 'Risks',
      field: 'risks',
      type: 'text',
      align: 'left',
      width: 80,
      sortable: false,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  },
  {
    label: 'Actions',
    sortable: false,
    align: 'center',
    subColumns: [{
      label: 'Actions',
      field: 'actions',
      type: 'text',
      align: 'left',
      width: 80,
      sortable: false,
      filterable: true,
    }],
    headerBgColor: '#1976d2',
    headerTextColor: '#ffffff',
  }
];

const SalesForecastCurrentQuarterTable = ({ classes, data, isLoading, asOfDateLabel, handleChangeLocation }) => {
  // Helper function to format date to MM/DD/YYYY using existing customFunctions
  const formatDateToMMDDYYYY = (dateString) => {
    if (!dateString || dateString === '') return '';
    try {
      // Use dateFormatterMomentV3 which returns MM/DD/YYYY
      return dateFormatterMomentV3(dateString);
    } catch (error) {
      return '';
    }
  };

  // Transform won opportunities data to rows format
  const wonOppsRows = useMemo(() => {
    if (!data?.wonOpps || data.wonOpps.length === 0) {
      return [];
    }
    return data.wonOpps.map((opp, index) => ({
      ...opp,
      label: opp.oppId || `won_${index}`,
    }));
  }, [data?.wonOpps]);

  // Transform forecast opportunities data to rows format
  const forecastOppsRows = useMemo(() => {
    if (!data?.forecastOpps || data.forecastOpps.length === 0) {
      return [];
    }
    return data.forecastOpps.map((opp, index) => ({
      ...opp,
      label: opp.oppId || `forecast_${index}`,
    }));
  }, [data?.forecastOpps]);
  return (
    <Box mt={4}>
      <Paper className={classes.paper}>
        <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {data?.title || 'Sales Forecast – Current Quarter'}
          </Typography>
          <span className={classes.updateFrequency}>
            {'Live Data'}
          </span>
        </Box>

        {isLoading ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Skeleton variant="rect" animation="wave" height={200} width="100%" />
            </Grid>
            <Grid item xs={12}>
              <Skeleton variant="rect" animation="wave" height={300} width="100%" />
            </Grid>
          </Grid>
        ) : (
          <>
            {/* Summary Table */}
            <Box mb={3}>
              <Typography variant="subtitle1" style={{ fontWeight: 600}}>
                Summary
              </Typography>
              <ResizableTable
                columns={createSummaryTableColumns()}
                rows={[
                  {
                    label: 'Won QTD',
                    count: data?.summary?.won?.count || 0,
                    acvInMillions: data?.summary?.won?.acvInMillions || '0.00',
                    rowType: 'won',
                  },
                  {
                    label: 'Most Likely + Commit',
                    count: data?.summary?.forecast?.count || 0,
                    acvInMillions: data?.summary?.forecast?.acvInMillions || '0.00',
                    rowType: 'forecast',
                  },
                  {
                    label: 'Total Forecast',
                    count: data?.summary?.total?.count || 0,
                    acvInMillions: data?.summary?.total?.acvInMillions || '0.00',
                    rowType: 'total',
                  },
                   {
                      label: 'Forecast Goal (1.5x Quota)',
                      count: '',
                      acvInMillions: data?.summary?.forecastGoal || '0.00',
                      rowType: 'forecast',
                    },
                  {
                    label: 'Quota',
                    count: '',
                    acvInMillions: data?.summary?.quotaInMillions || '0.00',
                    rowType: 'quota',
                  },
                ]}
                getCellValue={(row, field) => {
                  if (field === 'label') {
                    return row[field];
                  }
                  return row[field] != null ? row[field] : '';
                }}
                getCellStyle={(row, field) => {
                  const baseStyle = {};
                  if (field === 'label') {
                    baseStyle.fontWeight = 600;
                  }
                  if (row.rowType === 'total') {
                    baseStyle.backgroundColor = '#f5f5f5';
                    if (field === 'count' || field === 'acvInMillions') {
                      baseStyle.fontWeight = 600;
                    }
                  }
                  if (row.rowType === 'quota') {
                    baseStyle.backgroundColor = '#fff9c4';
                    if (field === 'acvInMillions') {
                      baseStyle.fontWeight = 600;
                    }
                  }
                  return baseStyle;
                }}
                rowKey="label"
                hideSubHeaderRow={true}
                maxWidth="34%"
              />
            </Box>

            {/* Won Opportunities Table */}
            <Box mb={3}>
              <Typography variant="subtitle1" style={{ fontWeight: 600, marginTop: 70 }}>
                Won Opportunities
              </Typography>
              {wonOppsRows.length > 0 ? (
                <ResizableTable
                  columns={createWonOppsColumns()}
                  rows={wonOppsRows}
                  getCellValue={(row, field, rowIndex) => {
                    // Always show sequential row numbers regardless of sort order
                    if (field === 'rowNum') {
                      return rowIndex != null ? rowIndex + 1 : row[field];
                    }
                    if (field === 'wonDate') {
                      return formatDateToMMDDYYYY(row[field]);
                    }
                    if (field === 'acvInK' && row[field] != null) {
                      return d3.format(",.1f")(row[field]);
                    }
                    if (field === 'ageSinceQualDays' && row[field] != null) {
                      return d3.format(",")(row[field]);
                    }
                    if (field === 'discountPercent') {
                      return row[field] != null ? row[field] : '';
                    }
                    return row[field] != null ? row[field] : '';
                  }}
                  getCellStyle={() => ({})}
                  rowKey="label"
                  hideSubHeaderRow={true}
                  maxWidth="100%"
                  onRowClick={(row) => handleChangeLocation && handleChangeLocation("drilldownOnGraph", row, 'currentQuarterWon')}
                />
              ) : (
                <Box style={{ textAlign: 'center', fontStyle: 'italic', color: '#666', padding: '20px' }}>
                  No won opportunities this quarter
                </Box>
              )}
            </Box>

            {/* Forecast Opportunities Table */}
            <Box>
              <Typography variant="subtitle1" style={{ fontWeight: 600, marginTop: 70 }}>
                Forecast Opportunities (Most Likely + Commit Opportunities)
              </Typography>
              {forecastOppsRows.length > 0 ? (
                <ResizableTable
                  columns={createForecastOppsColumns()}
                  rows={forecastOppsRows}
                  getCellValue={(row, field, rowIndex) => {
                    // Always show sequential row numbers regardless of sort order
                    if (field === 'rowNum') {
                      return rowIndex != null ? rowIndex + 1 : row[field];
                    }
                    if (field === 'projectedCloseDate') {
                      return formatDateToMMDDYYYY(row[field]);
                    }
                    if (field === 'acvInK' && row[field] != null) {
                      return d3.format(",.1f")(row[field]);
                    }
                    if (field === 'ageSinceQualDays' && row[field] != null) {
                      return d3.format(",")(row[field]);
                    }
                    if (field === 'discountPercent' || field === 'daysInStage') {
                      return row[field] != null ? row[field] : '';
                    }
                    if (field === 'actions' || field === 'risks') {
                      return row[field] != null ? row[field] : '';
                    }
                    return row[field] != null ? row[field] : '';
                  }}
                  getCellStyle={(row, field) => {
                    if (row.isNewThisWeek) {
                      return { backgroundColor: '#c8e6c9' };
                    }
                    if (field === 'actions') {
                      return { fontSize: '0.75rem' };
                    }
                    return {};
                  }}
                  rowKey="label"
                  hideSubHeaderRow={true}
                  maxWidth="100%"
                  onRowClick={(row) => handleChangeLocation && handleChangeLocation("drilldownOnGraph", row, 'currentQuarterForecast')}
                />
              ) : (
                <Box style={{ textAlign: 'center', fontStyle: 'italic', color: '#666', padding: '20px' }}>
                  No forecast opportunities for this quarter
                </Box>
              )}
            </Box>

            {/* Legend for green highlighting */}
            <Box mt={2}>
              <div className={classes.legend}>
                <div className={classes.legendItem}>
                  <div className={classes.legendBox} style={{ backgroundColor: '#c8e6c9' }} />
                  <Typography variant="body2">New this week</Typography>
                </div>
              </div>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default SalesForecastCurrentQuarterTable;
